import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Button, Badge } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import OrderService from '../../service/order.service';
import AccountService from '../../service/account.service';
import OrderCard from './OrderCard';
import { ApiContext } from '../../contexts/ApiContext';
import { accountPath } from '../../utils/accountPath';
import { useOrderAlerts } from '../../contexts/OrderAlertsContext';
import { isOrderStale } from '../../utils/orderAlertUtils';

const DEFAULT_COLUMNS = [
  { key: 'PENDING', title: 'Pendente' },
  { key: 'CONFIRMED', title: 'Confirmado' },
  { key: 'PREPARING', title: 'Preparando' },
  { key: 'READY_FOR_DELIVERY', title: 'Pronto para entrega' },
  { key: 'OUT_FOR_DELIVERY', title: 'Saiu para entrega' },
  { key: 'DELIVERED', title: 'Entregue' },
  { key: 'CANCELLED', title: 'Cancelado' },
];

const OrdersKanban = () => {
  const navigate = useNavigate();
  const { account } = useContext(ApiContext);
  const accountId = account?.id;
  const { subscribeOrderEvents } = useOrderAlerts();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [statuses, setStatuses] = useState([]);
  const [isDropping, setIsDropping] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [hoverDropTarget, setHoverDropTarget] = useState(null);
  const kanbanColumnsRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const dragMoveHandlerRef = useRef(null);
  const scrollHandlerRef = useRef(null);
  const lastPointerRef = useRef({ x: null, y: null });
  const draggingIdRef = useRef(null);

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current != null) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const clearDragListeners = () => {
    if (dragMoveHandlerRef.current) {
      window.removeEventListener('mousemove', dragMoveHandlerRef.current);
      window.removeEventListener('touchmove', dragMoveHandlerRef.current);
      dragMoveHandlerRef.current = null;
    }
    if (scrollHandlerRef.current && kanbanColumnsRef.current) {
      kanbanColumnsRef.current.removeEventListener('scroll', scrollHandlerRef.current);
      scrollHandlerRef.current = null;
    }
    stopAutoScroll();
  };

  const findColumnAtPoint = (x, y, draggableId) => {
    if (!kanbanColumnsRef.current) return null;

    const elements = document.elementsFromPoint(x, y);
    for (const el of elements) {
      if (el.getAttribute?.('data-rbd-draggable-id') === draggableId) continue;
      const colEl = el.closest?.('.kanban-column[data-status]');
      if (colEl && kanbanColumnsRef.current.contains(colEl)) return colEl;
    }

    const columnEls = kanbanColumnsRef.current.querySelectorAll('.kanban-column[data-status]');
    for (const colEl of columnEls) {
      const rect = colEl.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right) return colEl;
    }

    return null;
  };

  const resolveDropTarget = (pointer, draggableId) => {
    const { x, y } = pointer;
    if (x == null || y == null || !kanbanColumnsRef.current) return null;

    const colEl = findColumnAtPoint(x, y, draggableId);
    if (!colEl) return null;

    const droppableId = colEl.getAttribute('data-status');
    const droppable = colEl.querySelector('.kanban-droppable');
    let index = 0;

    if (droppable) {
      droppable.querySelectorAll('[data-rbd-draggable-id]').forEach((el) => {
        if (el.getAttribute('data-rbd-draggable-id') === draggableId) return;
        const cardRect = el.getBoundingClientRect();
        if (y > cardRect.top + cardRect.height / 2) index += 1;
      });
    }

    return { droppableId, index };
  };

  const updateDropHover = (pointer, draggableId) => {
    const target = resolveDropTarget(pointer, draggableId);
    setHoverDropTarget(target);
    return target;
  };

  const updateAutoScroll = (clientX) => {
    const el = kanbanColumnsRef.current;
    if (!el) return;

    const { left, right } = el.getBoundingClientRect();
    const edge = 72;
    const speed = 14;

    stopAutoScroll();

    let direction = 0;
    if (clientX < left + edge) direction = -1;
    else if (clientX > right - edge) direction = 1;

    if (direction !== 0) {
      autoScrollIntervalRef.current = setInterval(() => {
        el.scrollLeft += direction * speed;
        updateDropHover(lastPointerRef.current, draggingIdRef.current);
      }, 16);
    }
  };

  const resetDragState = () => {
    setDraggingId(null);
    setHoverDropTarget(null);
    draggingIdRef.current = null;
  };

  const onDragStart = (result) => {
    draggingIdRef.current = result.draggableId;
    setDraggingId(result.draggableId);
    lastPointerRef.current = { x: null, y: null };
    setHoverDropTarget(null);

    const handler = (e) => {
      const touch = e.touches ? e.touches[0] : null;
      const x = touch ? touch.clientX : e.clientX;
      const y = touch ? touch.clientY : e.clientY;
      if (typeof x === 'number' && typeof y === 'number') {
        lastPointerRef.current = { x, y };
        updateDropHover(lastPointerRef.current, draggingIdRef.current);
        updateAutoScroll(x);
      }
    };

    dragMoveHandlerRef.current = handler;
    window.addEventListener('mousemove', handler);
    window.addEventListener('touchmove', handler, { passive: true });

    const scrollHandler = () => {
      updateDropHover(lastPointerRef.current, draggingIdRef.current);
    };
    scrollHandlerRef.current = scrollHandler;
    kanbanColumnsRef.current?.addEventListener('scroll', scrollHandler, { passive: true });
  };

  useEffect(() => () => clearDragListeners(), []);

  const load = async (p = 1) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await OrderService.fetchOrders({ accountId, page: p, size: pageSize });
      setData(res.data || []);
      setPage(res.page || p);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error('Failed to load orders', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, [accountId]);

  useEffect(() => {
    if (!accountId) return undefined;

    AccountService.getOrderStatuses(accountId)
      .then((list) => {
        const sorted = (list || []).slice().sort((a, b) => a.order - b.order);
        setStatuses(sorted);
        const active = sorted.filter((s) => s.active);
        setColumns(active.length ? active.map((s) => ({ key: s.status, title: s.label })) : DEFAULT_COLUMNS);
      })
      .catch((err) => console.error('Failed to load order statuses', err));
  }, [accountId]);

  useEffect(() => {
    const unsubscribe = subscribeOrderEvents((type, order) => {
      if (!order) return;
      setData((prev) => {
        if (order.resolvedAt || order.resolved_at) {
          return prev.filter((o) => o.id !== order.id);
        }
        const exists = prev.some((o) => o.id === order.id);
        const withHighlight = { ...order, _highlight: Date.now() };
        if (type === 'created' && !exists) {
          return [withHighlight, ...prev];
        }
        if (exists) {
          return prev.map((o) => (o.id === order.id ? withHighlight : o));
        }
        if (page === 1) {
          return [withHighlight, ...prev];
        }
        return prev;
      });
    });
    return unsubscribe;
  }, [subscribeOrderEvents, page]);

  const grouped = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      const items = data
        .filter((d) => d.status === col.key)
        .slice()
        .sort((a, b) => {
          const ta = a && (a.updatedAt || a.updated_at || a.updatedAtAt || a.createdAt || a.created_at) || 0;
          const tb = b && (b.updatedAt || b.updated_at || b.updatedAtAt || b.createdAt || b.created_at) || 0;
          const da = typeof ta === 'number' ? ta : Date.parse(ta) || 0;
          const db = typeof tb === 'number' ? tb : Date.parse(tb) || 0;
          return db - da;
        });
      map[col.key] = items;
    });
    return map;
  }, [data, columns]);

  const staleCountByColumn = useMemo(() => {
    const counts = {};
    columns.forEach((col) => {
      counts[col.key] = (grouped[col.key] || []).filter((o) => isOrderStale(o)).length;
    });
    return counts;
  }, [grouped, columns]);

  const onDragEnd = async (result) => {
    clearDragListeners();

    const { source, destination, draggableId } = result;
    const { x, y } = lastPointerRef.current;
    const hasPointer = x != null && y != null;
    const pointerDestination = hasPointer
      ? resolveDropTarget(lastPointerRef.current, draggableId)
      : null;
    const finalDestination = pointerDestination || destination;

    resetDragState();

    if (!finalDestination) return;
    if (source.droppableId === finalDestination.droppableId && source.index === finalDestination.index) return;

    const prevData = data.slice();
    setIsDropping(true);

    const movedItemId = Number(draggableId);
    const originalMoved = prevData.find((d) => d.id === movedItemId);
    if (!originalMoved) {
      setIsDropping(false);
      return;
    }

    const withoutMoved = prevData.filter((d) => d.id !== movedItemId);

    const merged = [];
    columns.forEach((col) => {
      const itemsInCol = withoutMoved.filter((d) => d.status === col.key);
      if (col.key === finalDestination.droppableId) {
        const itemToInsert = { ...originalMoved, status: finalDestination.droppableId, _highlight: Date.now() };
        const insertIndex = Math.max(0, Math.min(finalDestination.index, itemsInCol.length));
        itemsInCol.splice(insertIndex, 0, itemToInsert);
      }
      merged.push(...itemsInCol);
    });

    const other = withoutMoved.filter((d) => !columns.some((c) => c.key === d.status));
    const finalFlat = [...merged, ...other];

    setData(finalFlat);

    setTimeout(() => setIsDropping(false), 350);

    setTimeout(() => {
      OrderService.updateOrder(originalMoved.id, { status: finalDestination.droppableId }).catch((err) => {
        console.error('Failed to update order status', err);
        load(page);
      });
    }, 0);
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Pedidos</h4>
                <Button color="primary" onClick={() => navigate(accountPath(accountId, '/pedidos/novo'))}>
                  <i className="tim-icons icon-simple-add" /> Novo Pedido
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {loading && <div>Carregando pedidos...</div>}

              <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div className="kanban-columns" ref={kanbanColumnsRef}>
                  {columns.map((col) => (
                    <div className="kanban-column" key={col.key} data-status={col.key}>
                      <Card className="mb-2">
                        <CardHeader style={{ fontWeight: 700 }}>
                          {col.title}{' '}
                          <small style={{ color: '#666' }}>({grouped[col.key]?.length || 0})</small>
                          {staleCountByColumn[col.key] > 0 && (
                            <Badge color="warning" pill className="ml-2 kanban-stale-badge">
                              {staleCountByColumn[col.key]} parado{staleCountByColumn[col.key] !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </CardHeader>
                        <CardBody style={{ minHeight: 240, maxHeight: '70vh', overflowY: 'auto' }}>
                          <Droppable droppableId={col.key}>
                            {(provided) => {
                              const items = grouped[col.key] || [];
                              const showPlaceholderAt = (idx) =>
                                draggingId &&
                                hoverDropTarget?.droppableId === col.key &&
                                hoverDropTarget.index === idx;

                              return (
                              <div
                                className={`kanban-droppable ${hoverDropTarget?.droppableId === col.key ? 'is-dragging-over' : ''}`}
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {items.map((order, index) => (
                                  <React.Fragment key={String(order.id)}>
                                    {showPlaceholderAt(index) && (
                                      <div className="kanban-drop-placeholder" aria-hidden="true" />
                                    )}
                                    <Draggable draggableId={String(order.id)} index={index} isDragDisabled={isDropping}>
                                      {(prov, snapshot) => {
                                        const dStyle = prov.draggableProps.style || {};
                                        const combined = { ...dStyle, marginBottom: 12, transition: 'transform 120ms ease, box-shadow 120ms ease', willChange: 'transform' };
                                        const draggingStyle = snapshot.isDragging ? { boxShadow: '0 10px 20px rgba(0,0,0,0.14)', zIndex: 9999, borderRadius: 6, transform: dStyle.transform } : {};
                                        return (
                                          <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} style={{ ...combined, ...draggingStyle }}>
                                            <OrderCard order={order} statuses={statuses} onChangeStatus={null} onUpdated={(id, newStatus) => {
                                              setData((prev) => prev.map((o) => (o.id === id ? { ...o, status: newStatus } : o)));
                                            }} onResolved={(id) => {
                                              setData((prev) => prev.filter((o) => o.id !== id));
                                            }} />
                                          </div>
                                        );
                                      }}
                                    </Draggable>
                                  </React.Fragment>
                                ))}
                                {showPlaceholderAt(items.length) && (
                                  <div className="kanban-drop-placeholder" aria-hidden="true" />
                                )}
                                <div className="kanban-rbd-placeholder">{provided.placeholder}</div>
                              </div>
                              );
                            }}
                          </Droppable>
                        </CardBody>
                      </Card>
                    </div>
                  ))}
                </div>
              </DragDropContext>

              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { if (page > 1) { load(page - 1); } }}>Anterior</button>
                <span style={{ alignSelf: 'center' }}>{page} / {totalPages}</span>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { if (page < totalPages) { load(page + 1); } }}>Próxima</button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrdersKanban;
